import { memo, useCallback, useMemo, useRef } from 'react';
import RewardsSection from '../RewardsSection/RewardsSection';
import { useSelector } from '@bonanza-social/toolkit/state';
import {
  selectCashbacks,
  selectCashbacksDataState,
} from '../../../../cashback/lib/state/cashback/cashback.slice';
import { CashbackCard } from '@bonanza-social/cashback';
import {
  Box,
  Breakpoint,
  List,
  ListProps,
  Skeleton,
  useBreakpoint,
  useBreakpointProp,
  VerticalListSeparator,
} from '@bonanza-social/moonlight';
import { ListRenderItem } from 'react-native';
import { CashbackPromotion } from '../../../../cashback/lib/state/cashback/cashback.types';
import { CashbackSectionProps } from './CashbackSection.types';
import { useListNavigation } from '@bonanza-social/navigation';

function CashbackSection({ itemWidth }: CashbackSectionProps) {
  const cashbacks = useSelector(selectCashbacks);
  const breakpoint = useBreakpoint();
  const dataState = useSelector(selectCashbacksDataState);

  const listRef = useRef(null);
  const containerRef = useRef(null);

  // useEffect(() => {
  //   dispatch(fetchCashback());
  // }, [dispatch]);

  const numColumns = useBreakpointProp({
    mobile: 1,
    tablet: 2,
    desktop: 3,
  });

  const isScrollable = useMemo(
    () => breakpoint === Breakpoint.MOBILE && cashbacks.length > 1,
    [breakpoint, cashbacks.length],
  );

  const renderItem = useCallback<ListRenderItem<CashbackPromotion>>(
    ({ item }) => {
      return (
        <CashbackCard
          width={{
            mobile: isScrollable ? itemWidth * 0.8 : '100%',
            tablet: itemWidth,
            desktop: itemWidth,
          }}
          cashbackPromo={item}
        />
      );
    },
    [itemWidth, isScrollable],
  );

  const { handleScroll, handleLayout, handleContentSizeChange } =
    useListNavigation({
      listRef,
      containerRef,
      itemWidth: undefined,
      showArrows: false,
      itemsCount: cashbacks.length,
      enableDragging: true,
      enableArrows: false,
    });

  const listProps = useMemo((): Partial<ListProps> => {
    return isScrollable
      ? {
          horizontal: true,
          onScroll: handleScroll,
          onContentSizeChange: handleContentSizeChange,
          contentContainerStyle: {
            gap: 'space-400',
          },
        }
      : {
          ItemSeparatorComponent: VerticalListSeparator,
          numColumns: numColumns,
          ...(numColumns > 1 && {
            columnWrapperStyle: {
              gap: 'space-400',
            },
          }),
        };
  }, [handleContentSizeChange, handleScroll, numColumns, isScrollable]);

  if (dataState.match('idle|pending')) {
    return <Skeleton height={248} />;
  }

  if (!cashbacks.length) {
    return null;
  }

  return (
    <RewardsSection titleLocKey="cashbackTitle">
      <Box ref={containerRef} onLayout={handleLayout}>
        <List
          ref={listRef}
          key={`${numColumns}-${isScrollable}`}
          data={cashbacks}
          showsHorizontalScrollIndicator={false}
          scrollEnabled={isScrollable}
          renderItem={renderItem}
          {...listProps}
        />
      </Box>
    </RewardsSection>
  );
}

export default memo(CashbackSection);
